import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getMedications, addMedication, deleteMedication, markMedicationTaken, getSupplementAudit } from '../services/api';
import toast from 'react-hot-toast';

const FREQUENCIES = [
  { value: 'once_daily', label: 'Once daily' },
  { value: 'twice_daily', label: 'Twice daily' },
  { value: 'three_times_daily', label: 'Three times daily' },
  { value: 'as_needed', label: 'As needed' },
  { value: 'weekly', label: 'Weekly' },
];

const TIMES = [
  { value: 'morning', label: 'Morning' },
  { value: 'afternoon', label: 'Afternoon' },
  { value: 'evening', label: 'Evening' },
  { value: 'bedtime', label: 'Bedtime' },
];

const FREQ_LABEL: Record<string, string> = {
  once_daily: 'Once daily',
  twice_daily: 'Twice daily',
  three_times_daily: '3× daily',
  as_needed: 'As needed',
  weekly: 'Weekly',
};

interface Medication {
  id: string;
  name: string;
  dosage: string;
  frequency: string;
  time_of_day: string[];
  notes: string;
  taken_today: boolean;
}

const EMPTY_FORM = { name: '', dosage: '', frequency: 'once_daily', time_of_day: ['morning'] as string[], notes: '' };

interface AuditFinding {
  medication: string;
  status: 'ok' | 'warning' | 'alert';
  recalls: { reason: string; classification: string; date: string; status: string }[];
  adverse_events_total: number;
  boxed_warning: string | null;
}

export default function MedicationsPage() {
  const { currentUser } = useAuth();
  const [meds, setMeds] = useState<Medication[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [today, setToday] = useState('');
  const [audit, setAudit] = useState<AuditFinding[] | null>(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditDate, setAuditDate] = useState('');

  const load = useCallback(async () => {
    if (!currentUser) return;
    setLoading(true);
    try {
      const token = await currentUser.getIdToken();
      const res = await getMedications(token);
      setMeds(res.medications || []);
      setToday(res.date || '');
    } catch {
      toast.error('Failed to load medications');
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  const runAudit = async () => {
    if (!currentUser) return;
    setAuditLoading(true);
    try {
      const token = await currentUser.getIdToken();
      const res = await getSupplementAudit(token);
      setAudit(res.findings || []);
      setAuditDate(res.audited_date || '');
      if (!res.cached) toast.success('Supplement audit complete');
    } catch {
      toast.error('Audit failed. Try again later.');
    } finally {
      setAuditLoading(false);
    }
  };

  useEffect(() => { load(); }, [load]);

  const handleTimeToggle = (t: string) => {
    setForm((prev) => ({
      ...prev,
      time_of_day: prev.time_of_day.includes(t)
        ? prev.time_of_day.filter((x) => x !== t)
        : [...prev.time_of_day, t],
    }));
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser || !form.name.trim()) return;
    setSaving(true);
    try {
      const token = await currentUser.getIdToken();
      await addMedication(token, form);
      toast.success(`${form.name} added`);
      setForm(EMPTY_FORM);
      setShowForm(false);
      load();
    } catch {
      toast.error('Failed to add medication');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleTaken = async (med: Medication) => {
    if (!currentUser) return;
    const prev = med.taken_today;
    // Optimistic update
    setMeds((ms) => ms.map((m) => m.id === med.id ? { ...m, taken_today: !prev } : m));
    try {
      const token = await currentUser.getIdToken();
      await markMedicationTaken(token, med.id);
    } catch {
      // Revert
      setMeds((ms) => ms.map((m) => m.id === med.id ? { ...m, taken_today: prev } : m));
      toast.error('Failed to update');
    }
  };

  const handleDelete = async (med: Medication) => {
    if (!currentUser) return;
    if (!window.confirm(`Remove ${med.name}?`)) return;
    try {
      const token = await currentUser.getIdToken();
      await deleteMedication(token, med.id);
      toast.success(`${med.name} removed`);
      setMeds((ms) => ms.filter((m) => m.id !== med.id));
    } catch {
      toast.error('Failed to remove medication');
    }
  };

  const takenCount = meds.filter((m) => m.taken_today).length;
  const totalCount = meds.filter((m) => m.frequency !== 'as_needed').length;
  const pct = totalCount > 0 ? Math.round((takenCount / totalCount) * 100) : null;

  return (
    <div className="max-w-2xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Medications</h1>
          <p className="text-gray-500 text-sm mt-1">Track your daily medications and supplements.</p>
        </div>
        <button
          onClick={() => setShowForm((v) => !v)}
          className="px-4 py-2 bg-emerald-600 text-white text-sm font-semibold rounded-lg hover:bg-emerald-700 transition-colors"
        >
          {showForm ? 'Cancel' : '+ Add'}
        </button>
      </div>

      {/* Today's adherence bar */}
      {meds.length > 0 && totalCount > 0 && (
        <div className="bg-white rounded-2xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-gray-700">Today's adherence</p>
            <p className="text-sm font-bold text-gray-900">{takenCount} / {totalCount}</p>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2.5">
            <div
              className={`h-2.5 rounded-full transition-all duration-500 ${
                pct === 100 ? 'bg-emerald-500' : pct && pct >= 50 ? 'bg-yellow-400' : 'bg-red-400'
              }`}
              style={{ width: `${pct ?? 0}%` }}
            />
          </div>
          {pct === 100 && (
            <p className="text-xs text-emerald-600 mt-2 font-medium">All medications taken today!</p>
          )}
        </div>
      )}

      {/* Add form */}
      {showForm && (
        <form onSubmit={handleAdd} className="bg-white rounded-2xl border border-gray-200 p-6 space-y-4">
          <h2 className="font-semibold text-gray-800">Add Medication</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <input
                type="text"
                required
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Metformin"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Dosage</label>
              <input
                type="text"
                value={form.dosage}
                onChange={(e) => setForm((f) => ({ ...f, dosage: e.target.value }))}
                placeholder="e.g. 500mg"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Frequency</label>
            <select
              value={form.frequency}
              onChange={(e) => setForm((f) => ({ ...f, frequency: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              {FREQUENCIES.map(({ value, label }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Time of day</label>
            <div className="flex flex-wrap gap-2">
              {TIMES.map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => handleTimeToggle(value)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    form.time_of_day.includes(value)
                      ? 'bg-emerald-600 text-white border-emerald-600'
                      : 'bg-white text-gray-600 border-gray-300 hover:border-emerald-400'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
            <input
              type="text"
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              placeholder="e.g. Take with food"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>

          <button
            type="submit"
            disabled={saving}
            className="w-full py-2.5 bg-emerald-600 text-white rounded-lg font-semibold text-sm hover:bg-emerald-700 disabled:opacity-50 transition-colors"
          >
            {saving ? 'Saving...' : 'Add Medication'}
          </button>
        </form>
      )}

      {/* Medications list */}
      {loading ? (
        <div className="flex items-center justify-center h-32 text-gray-400 text-sm">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600 mr-2" />
          Loading...
        </div>
      ) : meds.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-gray-300 p-10 text-center">
          <p className="text-3xl mb-2">💊</p>
          <p className="text-gray-500 text-sm">No medications added yet.</p>
          <button
            onClick={() => setShowForm(true)}
            className="mt-3 text-sm text-emerald-600 hover:text-emerald-800 font-medium transition-colors"
          >
            Add your first medication →
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {today && (
            <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">{today}</p>
          )}
          {meds.map((med) => (
            <div
              key={med.id}
              className={`bg-white rounded-2xl border p-4 flex items-center gap-4 transition-colors ${
                med.taken_today ? 'border-emerald-200 bg-emerald-50/30' : 'border-gray-200'
              }`}
            >
              {/* Taken toggle */}
              <button
                onClick={() => handleToggleTaken(med)}
                className={`flex-shrink-0 w-10 h-10 rounded-full border-2 flex items-center justify-center transition-all ${
                  med.taken_today
                    ? 'bg-emerald-500 border-emerald-500 text-white'
                    : 'border-gray-300 text-gray-300 hover:border-emerald-400'
                }`}
                title={med.taken_today ? 'Mark as not taken' : 'Mark as taken'}
              >
                {med.taken_today ? '✓' : ''}
              </button>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className={`font-semibold text-sm ${med.taken_today ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                    {med.name}
                  </p>
                  {med.dosage && (
                    <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{med.dosage}</span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  <span className="text-xs text-gray-400">{FREQ_LABEL[med.frequency] || med.frequency}</span>
                  {med.time_of_day.length > 0 && (
                    <>
                      <span className="text-gray-300">·</span>
                      <span className="text-xs text-gray-400">{med.time_of_day.join(', ')}</span>
                    </>
                  )}
                  {med.notes && (
                    <>
                      <span className="text-gray-300">·</span>
                      <span className="text-xs text-gray-400 italic">{med.notes}</span>
                    </>
                  )}
                </div>
              </div>

              {/* Delete */}
              <button
                onClick={() => handleDelete(med)}
                className="flex-shrink-0 text-gray-300 hover:text-red-400 transition-colors text-lg leading-none"
                title="Remove medication"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {/* ── Lex-Vital Supplement Audit ───────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <div className="flex items-start justify-between mb-1">
          <div>
            <h2 className="font-semibold text-gray-800">Safety Audit</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Checks your medications against the FDA database for recalls, adverse events, and boxed warnings.
              {auditDate && <span className="ml-1 text-gray-400">Last run: {auditDate}</span>}
            </p>
          </div>
          <button
            onClick={runAudit}
            disabled={auditLoading}
            className="flex-shrink-0 px-4 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors ml-4"
          >
            {auditLoading ? 'Scanning…' : 'Run Audit'}
          </button>
        </div>

        {audit !== null && (
          <div className="mt-4 space-y-3">
            {audit.length === 0 ? (
              <p className="text-sm text-gray-500 italic">No medications to audit yet. Add them above.</p>
            ) : (
              audit.map((f) => (
                <div
                  key={f.medication}
                  className={`rounded-xl p-4 border ${
                    f.status === 'alert'   ? 'border-red-300 bg-red-50' :
                    f.status === 'warning' ? 'border-amber-300 bg-amber-50' :
                                            'border-gray-200 bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-base">
                      {f.status === 'alert' ? '🚨' : f.status === 'warning' ? '⚠️' : '✓'}
                    </span>
                    <span className="font-semibold text-sm text-gray-900">{f.medication}</span>
                    <span className={`ml-auto text-xs font-medium px-2 py-0.5 rounded-full ${
                      f.status === 'alert'   ? 'bg-red-100 text-red-700' :
                      f.status === 'warning' ? 'bg-amber-100 text-amber-700' :
                                              'bg-green-100 text-green-700'
                    }`}>
                      {f.status === 'alert' ? 'RECALL FOUND' : f.status === 'warning' ? 'Review' : 'Clear'}
                    </span>
                  </div>

                  {f.recalls.length > 0 && (
                    <div className="mt-2">
                      {f.recalls.map((r, i) => (
                        <p key={i} className="text-xs text-red-700 mt-1">
                          <strong>Recall ({r.classification})</strong> {r.date}: {r.reason}
                        </p>
                      ))}
                    </div>
                  )}

                  {f.boxed_warning && (
                    <p className="text-xs text-amber-800 mt-1">
                      <strong>Boxed Warning:</strong> {f.boxed_warning.slice(0, 200)}{f.boxed_warning.length > 200 ? '…' : ''}
                    </p>
                  )}

                  {f.adverse_events_total > 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      {f.adverse_events_total.toLocaleString()} adverse event reports on FDA record.
                    </p>
                  )}
                </div>
              ))
            )}
            <p className="text-xs text-gray-400 pt-1">
              Data from openFDA. Not a substitute for professional medical advice. Cached for 30 days.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
